import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WatchPage from './WatchPage';
import { api, unwrap } from '../api';
import { resolvePath } from '../api/resolve';
import analytics from '../services/analytics';
import './ContentRouter.css';

/**
 * Resolves a /watch key against the CMS: `GET /v1/media/{idOrSlug}` (a retired slug answers with
 * the current one), else `GET /v1/resolve` for a legacy path or Webflow item id.
 * @param {string} key media id or slug
 * @param {string} [path] the legacy path to resolve, default `/watch/<key>`
 * @returns {Promise<{page?: object, redirect?: string, notFound?: boolean, blocked?: string, failed?: boolean}>}
 */
export async function resolveWatchSlug(key, path = `/watch/${key}`) {
  try {
    const { data } = await unwrap(
      api.GET('/v1/media/{idOrSlug}', { params: { path: { idOrSlug: key } } }),
    );
    return data.media.slug && data.media.slug !== key
      ? { page: data, redirect: `/watch/${data.media.slug}` }
      : { page: data };
  } catch (error) {
    if (error.code === 'GEO_BLOCKED') {
      return { blocked: 'This title is not available in your region.' };
    }
    if (['NOT_PUBLISHED', 'NOT_YET_AVAILABLE', 'DEVICE_NOT_ALLOWED'].includes(error.code)) {
      return { blocked: 'This title is not available right now.' };
    }
    if (error.code !== 'NOT_FOUND') {
      console.error('ContentRouter: GET /v1/media failed:', error);
      return { failed: true };
    }
  }
  const redirect = await resolvePath(path);
  return redirect ? { redirect } : { notFound: true };
}

/**
 * `/watch/:channelSlug[/:episodeId]`: resolves the v1 media and renders the watch page. A legacy
 * series-episode link plays the episode when v1 knows its id, else resolves the full path.
 */
const ContentRouter = () => {
  const { channelSlug, episodeId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const viewedRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setResult(null);
    const key = episodeId || channelSlug;
    const path = episodeId ? `/watch/${channelSlug}/${episodeId}` : `/watch/${channelSlug}`;
    resolveWatchSlug(key, path).then(resolved => {
      if (cancelled) {
        return;
      }
      if (resolved.redirect) {
        navigate(`${resolved.redirect}${window.location.search}`, { replace: true });
        return;
      }
      setResult(resolved);
      const media = resolved.page?.media;
      if (media && viewedRef.current !== media.id) {
        // Watch-page view (pixel-only): the entry event of the video journey.
        viewedRef.current = media.id;
        analytics.trackVideoView({
          _id: media.id,
          slug: media.slug,
          title: media.title?.value,
          content_type: media.kind,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [channelSlug, episodeId, attempt, navigate]);

  if (!result) {
    return (
      <div className="content-router-loading">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  if (result.page) {
    return <WatchPage key={result.page.media.id} page={result.page} />;
  }

  const message = result.notFound
    ? "We couldn't find this video."
    : result.blocked || "We couldn't load this content right now. Please try again.";
  return (
    <div className="content-router-error">
      <div className="error-container">
        <h2>{result.blocked ? 'Not Available' : 'Content Not Found'}</h2>
        <p>{message}</p>
        <div className="error-actions">
          {result.failed && (
            <button onClick={() => setAttempt(a => a + 1)} className="btn-secondary">
              Try Again
            </button>
          )}
          <button onClick={() => navigate('/')} className="btn-primary">
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentRouter;
